'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Users,
  PieChart,
  Globe,
  ChevronDown,
  Menu,
  X,
  Star,
  Check,
  ArrowRight,
  Shield,
  Smartphone,
  BarChart3,
  Lock,
  Headphones,
} from 'lucide-react';

/* ───────────────────────── DATA ───────────────────────── */

const tickers = [
  { symbol: 'EUR/USD', price: '1.0847', change: '+0.12%' },
  { symbol: 'GBP/USD', price: '1.2634', change: '+0.08%' },
  { symbol: 'USD/JPY', price: '149.82', change: '-0.15%' },
  { symbol: 'BTC/USD', price: '87,245', change: '+2.34%' },
  { symbol: 'ETH/USD', price: '3,421', change: '+1.87%' },
  { symbol: 'Gold', price: '2,987.50', change: '+0.45%' },
  { symbol: 'S&P 500', price: '5,842.3', change: '+0.32%' },
  { symbol: 'DAX', price: '18,472', change: '-0.21%' },
  { symbol: 'USD/CHF', price: '0.8823', change: '+0.05%' },
  { symbol: 'AUD/USD', price: '0.6543', change: '-0.18%' },
  { symbol: 'Crude Oil', price: '78.34', change: '+0.67%' },
  { symbol: 'SOL/USD', price: '142.80', change: '+3.12%' },
];

const stats = [
  { value: '10+', label: 'Years of Experience' },
  { value: '25K+', label: 'Satisfied Customers' },
  { value: '180+', label: 'Trading Instruments' },
  { value: '<12ms', label: 'Execution Speed' },
];

const features = [
  {
    icon: 'TrendingUp',
    title: 'High Profit Potential',
    description:
      'Leverage market movements across multiple asset classes to maximize your portfolio returns with professional-grade tools.',
  },
  {
    icon: 'Users',
    title: 'Accessibility for All',
    description:
      "Whether you're a beginner or a seasoned professional, our platform adapts to your experience level with intuitive tools.",
  },
  {
    icon: 'PieChart',
    title: 'Portfolio Diversification',
    description:
      'Spread your investments across Forex, Stocks, Indices, Commodities and Crypto — all from a single account.',
  },
  {
    icon: 'Globe',
    title: 'Global Liquidity',
    description:
      'Access deep institutional liquidity pools ensuring tight spreads and fast execution across all market conditions.',
  },
];

const markets = [
  {
    name: 'Forex',
    pairs: '60+ pairs',
    spreads: 'From 0.0 pips',
    leverage: 'Up to 1:500',
    description: 'Major, minor and exotic currency pairs with deep liquidity',
    examples: ['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/CAD'],
    color: '#00B8D9',
  },
  {
    name: 'Stocks',
    pairs: '500+ CFDs',
    spreads: 'From 0.1%',
    leverage: 'Up to 1:20',
    description: 'Trade global equities from NYSE, NASDAQ, LSE, and more',
    examples: ['Apple', 'Tesla', 'Amazon', 'NVIDIA'],
    color: '#00B8D9',
  },
  {
    name: 'Indices',
    pairs: '20+ indices',
    spreads: 'From 0.4 pts',
    leverage: 'Up to 1:200',
    description: "Access the world's major stock indices in a single click",
    examples: ['S&P 500', 'DAX 40', 'FTSE 100', 'Nikkei 225'],
    color: '#00B8D9',
  },
  {
    name: 'Commodities',
    pairs: '15+ instruments',
    spreads: 'From 0.03',
    leverage: 'Up to 1:100',
    description: 'Precious metals, energy and soft commodities',
    examples: ['Gold', 'Silver', 'Crude Oil', 'Natural Gas'],
    color: '#00B8D9',
  },
  {
    name: 'Crypto',
    pairs: '30+ pairs',
    spreads: 'From 0.1%',
    leverage: 'Up to 1:10',
    description: 'Trade the top cryptocurrencies 24/7 with competitive spreads',
    examples: ['BTC/USD', 'ETH/USD', 'SOL/USD', 'XRP/USD'],
    color: '#00B8D9',
  },
];

const accounts = [
  {
    name: 'Standard',
    minDeposit: '$10,000',
    popular: false,
    features: [
      '180+ trading instruments',
      'Personal account manager',
      'Daily market analysis',
      'Email & chat support',
      'Demo account included',
      'Mobile trading app',
    ],
  },
  {
    name: 'Silver',
    minDeposit: '$50,000',
    popular: false,
    features: [
      'Everything in Standard',
      'Dedicated senior analyst',
      'Custom trading strategies',
      'Reduced spreads',
      'Monthly portfolio review',
      'Economic calendar alerts',
    ],
  },
  {
    name: 'Gold',
    minDeposit: '$100,000',
    popular: true,
    features: [
      'Everything in Silver',
      'Raw spreads access',
      'VIP event invitations',
      'Tax optimization guidance',
      'Priority withdrawals',
      'One-on-one coaching',
    ],
  },
  {
    name: 'VIP',
    minDeposit: '$250,000',
    popular: false,
    features: [
      'Everything in Gold',
      'Zero spread on majors',
      'Institutional liquidity',
      'Custom API access',
      'Dedicated trading desk',
      'Concierge support 24/7',
    ],
  },
];

const testimonials = [
  {
    name: 'Alexander Braun',
    role: 'Professional Trader',
    text: "VertexCFD execution speed is on par with institutional platforms I've used. The raw spreads on EUR/USD are consistently competitive, even during high-volatility events.",
    rating: 5,
  },
  {
    name: 'Marie Johansson',
    role: 'Portfolio Manager',
    text: 'The multi-asset access from a single account is what sold me. I manage forex, commodities, and equity CFDs seamlessly — the charting tools are genuinely professional-grade.',
    rating: 5,
  },
  {
    name: 'David Chen',
    role: 'Algorithmic Trader',
    text: "The API is well-documented and the latency is excellent. I've migrated three algo strategies to VertexCFD and the fill quality improvement was immediately measurable.",
    rating: 5,
  },
  {
    name: 'Sofia Petrova',
    role: 'Risk Analyst',
    text: 'Impressed by the transparency — no hidden fees, clear margin requirements, and the risk tools are built into the platform rather than afterthoughts.',
    rating: 5,
  },
];

const faqs = [
  {
    q: 'What is VertexCFD?',
    a: 'VertexCFD is a regulated online trading platform providing access to Forex, Stocks, Indices, Commodities and Crypto CFDs. We combine institutional-grade technology with competitive pricing and dedicated client support.',
  },
  {
    q: 'How do I open a trading account?',
    a: "Click 'Start Trading', complete the registration form with your personal details, verify your identity (KYC), and fund your account. The entire process takes less than 10 minutes and you can start trading immediately.",
  },
  {
    q: 'What is the minimum deposit?',
    a: 'Our Standard account requires a minimum deposit of $10,000. Higher-tier accounts — Silver ($50,000), Gold ($100,000), and VIP ($250,000) — offer progressively tighter spreads, dedicated support, and premium services.',
  },
  {
    q: 'Is my money safe with VertexCFD?',
    a: 'Yes. Client funds are held in segregated accounts at top-tier European banks, completely separate from company operational funds. We also offer negative balance protection and are regulated by financial authorities.',
  },
  {
    q: 'What trading platforms do you offer?',
    a: 'We offer our proprietary web-based platform, desktop applications for Windows and Mac, and fully-featured mobile apps for iOS and Android. All platforms include advanced charting, one-click trading, and real-time market data.',
  },
  {
    q: 'How can I withdraw my funds?',
    a: 'Withdrawals can be made via bank wire transfer, credit/debit card, or cryptocurrency. Requests are processed within 1-2 business days. There are no fees on standard withdrawals.',
  },
  {
    q: 'What spreads do you offer?',
    a: 'Spreads vary by account tier. Standard accounts start from competitive market spreads, while VIP accounts offer raw spreads from 0.0 pips with institutional liquidity. Actual spreads depend on market conditions.',
  },
  {
    q: 'Do you offer educational resources?',
    a: 'Yes. We provide trading webinars, video tutorials, market analysis, economic calendar, and dedicated account managers who can guide you through your trading journey.',
  },
];

const footerLinks: Record<string, { label: string; href: string }[]> = {
  'Forex Trading': [
    { label: 'What is Forex', href: '#' },
    { label: 'How to Trade Forex', href: '#' },
    { label: 'EUR/USD', href: '#' },
    { label: 'GBP/USD', href: '#' },
    { label: 'Forex Spreads', href: '#' },
  ],
  'CFD Trading': [
    { label: 'What are CFDs', href: '#' },
    { label: 'Stock CFDs', href: '#' },
    { label: 'Index CFDs', href: '#' },
    { label: 'Gold Trading', href: '#' },
    { label: 'Oil Trading', href: '#' },
  ],
  Crypto: [
    { label: 'Bitcoin Trading', href: '#' },
    { label: 'Ethereum Trading', href: '#' },
    { label: 'Crypto CFDs', href: '#' },
    { label: 'Crypto Spreads', href: '#' },
  ],
  'Trading Info': [
    { label: 'Account Types', href: '#' },
    { label: 'Deposits & Withdrawals', href: '#' },
    { label: 'Leverage & Margin', href: '#' },
    { label: 'Trading Conditions', href: '#' },
  ],
  About: [
    { label: 'About VertexCFD', href: '#' },
    { label: 'Regulation & Licences', href: '#' },
    { label: 'Security of Funds', href: '#' },
    { label: 'Contact Us', href: '#' },
    { label: 'Legal Documents', href: '#' },
  ],
};

const iconMap: Record<string, React.ElementType> = { TrendingUp, Users, PieChart, Globe };

/* ───────────────────────── PAGE ───────────────────────── */

export default function VertexPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const navLinks = [
    { label: 'Markets', href: '#markets' },
    { label: 'Accounts', href: '#accounts' },
    { label: 'Platform', href: '#platform' },
    { label: 'About', href: '#about' },
  ];

  return (
    <div className="min-h-screen bg-white text-[#1A2B4A]">
      {/* ── Back to gallery ── */}
      <a
        href="/templates/"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 md:bottom-auto md:top-4 md:left-4 md:translate-x-0 z-[200] flex items-center gap-1.5 bg-black/70 hover:bg-black/90 backdrop-blur-sm text-white text-xs font-semibold px-4 py-2 rounded-full transition-all duration-200 shadow-xl border border-white/10"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        <span className="hidden sm:inline">All Templates</span>
        <span className="sm:hidden">Templates</span>
      </a>

      {/* ── HEADER ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm' : 'bg-[#0B2545] border-b border-white/10'
        }`}
      >
        <div className="site-container flex items-center justify-between h-16">
          <Link href="/templates/vertex" className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${scrolled ? 'bg-[#0B2545]' : 'bg-white/10'}`}
            >
              <BarChart3 className="w-5 h-5 text-[#00B8D9]" />
            </div>
            <span className={`text-xl font-bold ${scrolled ? 'text-[#0B2545]' : 'text-white'}`}>
              Vertex<span className="text-[#00B8D9]">CFD</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#4A5568] hover:text-[#0B2545]' : 'text-white/70 hover:text-white'}`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link
              href="#"
              className={`text-sm font-medium ${scrolled ? 'text-[#4A5568] hover:text-[#0B2545]' : 'text-white/70 hover:text-white'}`}
            >
              Log In
            </Link>
            <Link
              href="#"
              className="bg-[#00B8D9] hover:bg-[#00A3C4] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
            >
              Start Trading
            </Link>
          </div>

          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden bg-white border-t border-[#E2E8F0] overflow-hidden"
            >
              <div className="site-container py-4 flex flex-col gap-3">
                {navLinks.map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    className="text-sm font-medium text-[#4A5568] py-2"
                    onClick={() => setMobileOpen(false)}
                  >
                    {l.label}
                  </a>
                ))}
                <Link
                  href="#"
                  className="bg-[#00B8D9] text-white text-sm font-semibold px-5 py-2.5 rounded-lg text-center mt-2"
                >
                  Start Trading
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── HERO (navy dark) ── */}
      <section className="bg-[#0B2545] pt-28 pb-20 relative overflow-hidden">
        {/* subtle radial glow */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00B8D9]/8 rounded-full blur-[120px]" />

        <div className="site-container relative z-10 grid md:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#00B8D9] text-sm font-semibold tracking-wide uppercase mb-4">
              Trusted & Regulated Broker
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold text-white leading-tight mb-6">
              Trade CFDs on
              <br />
              <span className="text-[#00B8D9]">Global Markets</span>
            </h1>
            <p className="text-[#94A3B8] text-lg leading-relaxed mb-8 max-w-lg">
              Access Forex, Stocks, Indices, Commodities and Crypto — all from one
              institutional-grade platform. Tight spreads, lightning execution.
            </p>
            <div className="flex flex-wrap gap-4 mb-10">
              <Link
                href="#"
                className="bg-[#00B8D9] hover:bg-[#00A3C4] text-white font-semibold px-8 py-3.5 rounded-lg transition-colors inline-flex items-center gap-2"
              >
                Start Trading <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="#"
                className="border border-white/20 text-white hover:bg-white/5 font-semibold px-8 py-3.5 rounded-lg transition-colors"
              >
                Try Free Demo
              </Link>
            </div>
            {/* Trust row */}
            <div className="flex flex-wrap gap-6 text-sm text-[#94A3B8]">
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#00B8D9]" /> Regulated Broker
              </span>
              <span className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#00B8D9]" /> Segregated Funds
              </span>
              <span className="flex items-center gap-2">
                <Headphones className="w-4 h-4 text-[#00B8D9]" /> 24/5 Support
              </span>
            </div>
          </motion.div>

          {/* Right — Phone mockup */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center"
          >
            <div className="relative">
              {/* Phone frame */}
              <div className="w-[280px] h-[560px] bg-[#0D3059] rounded-[40px] border-[3px] border-[#1A4070] p-3 shadow-2xl shadow-[#00B8D9]/10">
                <div className="w-full h-full bg-[#091D36] rounded-[32px] overflow-hidden flex flex-col">
                  {/* Phone header */}
                  <div className="bg-[#0B2545] px-4 py-3 flex items-center justify-between border-b border-white/5">
                    <span className="text-white text-xs font-bold">VertexCFD</span>
                    <span className="text-[#00B8D9] text-[10px] font-medium">LIVE</span>
                  </div>
                  {/* Chart area */}
                  <div className="flex-1 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white text-sm font-semibold">EUR/USD</span>
                      <span className="text-[#10B981] text-xs font-medium">+0.12%</span>
                    </div>
                    <div className="text-white text-2xl font-bold mb-4">1.0847</div>
                    <svg viewBox="0 0 240 120" className="w-full">
                      <defs>
                        <linearGradient id="vGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#00B8D9" stopOpacity="0.25" />
                          <stop offset="100%" stopColor="#00B8D9" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M0,80 Q20,75 40,65 T80,55 T120,40 T160,50 T200,30 T240,20"
                        fill="none"
                        stroke="#00B8D9"
                        strokeWidth="2"
                      />
                      <path
                        d="M0,80 Q20,75 40,65 T80,55 T120,40 T160,50 T200,30 T240,20 L240,120 L0,120 Z"
                        fill="url(#vGrad)"
                      />
                    </svg>
                    {/* Mini instruments */}
                    <div className="mt-4 space-y-2">
                      {[
                        { s: 'GBP/USD', p: '1.2634', c: '+0.08%' },
                        { s: 'Gold', p: '2,987.50', c: '+0.45%' },
                        { s: 'BTC/USD', p: '87,245', c: '+2.34%' },
                      ].map((t) => (
                        <div
                          key={t.s}
                          className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2"
                        >
                          <span className="text-white/80 text-xs">{t.s}</span>
                          <span className="text-white text-xs font-medium">{t.p}</span>
                          <span className="text-[#10B981] text-[10px]">{t.c}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Buy/Sell */}
                  <div className="px-3 pb-4 grid grid-cols-2 gap-2">
                    <button className="bg-[#10B981] text-white text-xs font-bold py-2.5 rounded-lg">
                      BUY
                    </button>
                    <button className="bg-[#EF4444] text-white text-xs font-bold py-2.5 rounded-lg">
                      SELL
                    </button>
                  </div>
                </div>
              </div>
              {/* Floating badge */}
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -left-12 top-20 bg-white rounded-xl px-4 py-3 shadow-lg shadow-black/10"
              >
                <p className="text-xs text-[#64748B]">Execution</p>
                <p className="text-lg font-bold text-[#0B2545]">&lt;12ms</p>
              </motion.div>
              <motion.div
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 3.5, repeat: Infinity }}
                className="absolute -right-10 bottom-28 bg-white rounded-xl px-4 py-3 shadow-lg shadow-black/10"
              >
                <p className="text-xs text-[#64748B]">Instruments</p>
                <p className="text-lg font-bold text-[#0B2545]">180+</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── TICKER ── */}
      <div className="bg-[#F7FAFC] border-y border-[#E2E8F0] py-3 overflow-hidden">
        <div className="ticker-animate flex gap-8 whitespace-nowrap">
          {[...tickers, ...tickers].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-2 text-sm">
              <span className="font-semibold text-[#1A2B4A]">{t.symbol}</span>
              <span className="text-[#64748B]">{t.price}</span>
              <span className={t.change.startsWith('+') ? 'text-[#10B981]' : 'text-[#EF4444]'}>
                {t.change}
              </span>
            </span>
          ))}
        </div>
      </div>

      {/* ── STATS ── */}
      <section className="site-section">
        <div className="site-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="text-center py-8"
              >
                <p className="text-4xl md:text-5xl font-bold text-[#0B2545] mb-2">{s.value}</p>
                <p className="text-sm text-[#64748B]">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="bg-[#F7FAFC] site-section" id="about">
        <div className="site-container">
          <div className="text-center mb-14">
            <p className="text-[#00B8D9] text-sm font-semibold uppercase tracking-wide mb-3">
              Why VertexCFD
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B2545]">
              Your Trusted Trading Partner
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((f, i) => {
              const Icon = iconMap[f.icon] || TrendingUp;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="bg-white rounded-xl p-8 border border-[#E2E8F0] hover:border-[#00B8D9]/30 hover:shadow-md transition-all duration-300 flex gap-5"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#00B8D9]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-[#00B8D9]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0B2545] mb-2">{f.title}</h3>
                    <p className="text-[#64748B] text-sm leading-relaxed">{f.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── MARKETS ── */}
      <section className="site-section" id="markets">
        <div className="site-container">
          <div className="text-center mb-14">
            <p className="text-[#00B8D9] text-sm font-semibold uppercase tracking-wide mb-3">
              Markets
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B2545]">
              Trade Global Instruments
            </h2>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {markets.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-white rounded-xl border border-[#E2E8F0] hover:border-[#00B8D9]/30 hover:shadow-md transition-all duration-300 overflow-hidden"
              >
                <div className="h-1" style={{ backgroundColor: m.color }} />
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#0B2545] mb-1">{m.name}</h3>
                  <p className="text-xs text-[#64748B] mb-4">{m.description}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8]">Instruments</span>
                      <span className="font-medium text-[#1A2B4A]">{m.pairs}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8]">Spreads</span>
                      <span className="font-medium text-[#1A2B4A]">{m.spreads}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8]">Leverage</span>
                      <span className="font-medium text-[#1A2B4A]">{m.leverage}</span>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {m.examples.map((e) => (
                      <span
                        key={e}
                        className="text-[10px] bg-[#F7FAFC] text-[#64748B] px-2 py-1 rounded-md border border-[#E2E8F0]"
                      >
                        {e}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ACCOUNTS ── */}
      <section className="bg-[#F7FAFC] site-section" id="accounts">
        <div className="site-container">
          <div className="text-center mb-14">
            <p className="text-[#00B8D9] text-sm font-semibold uppercase tracking-wide mb-3">
              Account Types
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B2545]">
              Choose Your Trading Tier
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {accounts.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className={`relative bg-white rounded-xl border p-8 transition-all duration-300 ${
                  a.popular
                    ? 'border-[#00B8D9] shadow-lg shadow-[#00B8D9]/10 scale-[1.02]'
                    : 'border-[#E2E8F0] hover:border-[#00B8D9]/30'
                }`}
              >
                {a.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00B8D9] text-white text-xs font-semibold px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold text-[#0B2545] mb-1">{a.name}</h3>
                <p className="text-2xl font-bold text-[#00B8D9] mb-6">{a.minDeposit}</p>
                <ul className="space-y-3 mb-8">
                  {a.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm text-[#4A5568]">
                      <Check className="w-4 h-4 text-[#00B8D9] flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  href="#"
                  className={`block text-center text-sm font-semibold py-3 rounded-lg transition-colors ${
                    a.popular
                      ? 'bg-[#00B8D9] text-white hover:bg-[#00A3C4]'
                      : 'border border-[#00B8D9] text-[#00B8D9] hover:bg-[#00B8D9]/5'
                  }`}
                >
                  Get Started
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="site-section">
        <div className="site-container">
          <div className="text-center mb-14">
            <p className="text-[#00B8D9] text-sm font-semibold uppercase tracking-wide mb-3">
              Testimonials
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B2545]">
              Trusted by Traders Worldwide
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-white rounded-xl border border-[#E2E8F0] p-8 hover:shadow-md transition-all"
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, si) => (
                    <Star key={si} className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
                  ))}
                </div>
                <p className="text-[#4A5568] text-sm leading-relaxed mb-6">
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0B2545] flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {t.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0B2545]">{t.name}</p>
                    <p className="text-xs text-[#64748B]">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-[#F7FAFC] site-section">
        <div className="site-container">
          <div className="text-center mb-14">
            <p className="text-[#00B8D9] text-sm font-semibold uppercase tracking-wide mb-3">FAQ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B2545]">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-semibold text-[#0B2545] text-sm">{f.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-[#00B8D9] flex-shrink-0 transition-transform duration-300 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-sm text-[#64748B] leading-relaxed">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="site-section">
        <div className="site-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#0B2545] rounded-2xl p-12 md:p-16 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#00B8D9]/10 rounded-full blur-[100px]" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Start Trading with VertexCFD Today
              </h2>
              <p className="text-[#94A3B8] text-lg mb-8 max-w-xl mx-auto">
                Open your account in minutes and access global markets with institutional-grade
                tools and competitive pricing.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <Link
                  href="#"
                  className="bg-[#00B8D9] hover:bg-[#00A3C4] text-white font-semibold px-8 py-3.5 rounded-lg transition-colors inline-flex items-center gap-2"
                >
                  Open Live Account <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="#"
                  className="border border-white/20 text-white hover:bg-white/5 font-semibold px-8 py-3.5 rounded-lg transition-colors"
                >
                  Try Demo Account
                </Link>
              </div>
              <p className="text-xs text-[#94A3B8]">
                No hidden fees &bull; Instant execution &bull; Regulated broker &bull; 24/5 support
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#0B2545] pt-16 pb-8">
        <div className="site-container">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
            {Object.entries(footerLinks).map(([cat, links]) => (
              <div key={cat}>
                <h4 className="text-white font-semibold text-sm mb-4">{cat}</h4>
                <ul className="space-y-2">
                  {links.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="text-[#94A3B8] text-sm hover:text-white transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="border-t border-white/10 pt-8 mb-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-[#00B8D9]" />
              </div>
              <span className="text-white font-bold">
                Vertex<span className="text-[#00B8D9]">CFD</span>
              </span>
            </div>
            <div className="flex gap-4">
              {/* Social icons */}
              {[
                'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
                'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
                'M11.944 0A12 12 0 000 12a12 12 0 0012 24 12 12 0 0012-12A12.02 12.02 0 0012 0h-.056zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z',
              ].map((path, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                >
                  <svg className="w-4 h-4 fill-[#94A3B8]" viewBox="0 0 24 24">
                    <path d={path} />
                  </svg>
                </a>
              ))}
            </div>
          </div>

          <div className="border-t border-white/10 pt-6">
            <p className="text-xs text-[#64748B] leading-relaxed mb-4">
              <strong className="text-[#94A3B8]">Risk Warning:</strong> Trading Forex and CFDs
              involves significant risk and can result in the loss of your invested capital. You
              should not invest more than you can afford to lose and should ensure that you fully
              understand the risks involved. Trading leveraged products may not be suitable for all
              investors. Past performance is not indicative of future results.
            </p>
            <div className="flex flex-wrap gap-4 text-xs text-[#64748B]">
              <a href="#" className="hover:text-white transition-colors">
                Terms & Conditions
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Risk Disclosure
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Cookie Policy
              </a>
            </div>
            <p className="text-xs text-[#4A5568] mt-4">
              &copy; 2026 VertexCFD. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
